import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import 'leaflet.markercluster';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { colors } from '@karnott/colors';
import { LAYER_API_BASE } from '../../../../constants';
import { CLUSTER_CONSTANTS } from '../../../../constants/clusterConstants';
import { PARCELS_CONSTANTS } from '../../../../constants/parcelsConstants';
import { I18nContext } from '../../../../contexts/I18nProvider';
import { UIContext } from '../../../../contexts/UIProvider';
import { useParcelColoration } from '../../../../effects/ParcelEffects';
import { selectUseIgnTiles } from '../../../../slices/ui';
import { useAppSelector } from '../../../../store/hooks';
import { color, zIndex } from '../../../../ui/theme';
import { formatArea, getStaticHTMLFromComponent, getTotal } from '../../../../utils';
import { L as Leaflet, setSmoothZoom } from '../../../../utils/LeafletOverrides';
import { isPluginActivated } from '../../../../utils/clusters';
import { inertFeatures } from '../../../../utils/map';
import { parcelArea } from '../../../../utils/parcelArea';
import { useDebounce } from '../../effects';
import './parcel-marker-cluster.css';
import { buildParcelStyle } from './style';
import { ParcelName } from './tooltip';

const defaultIcon = Leaflet.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
});

Leaflet.Marker.prototype.options.icon = defaultIcon;

export const parcelPropertiesDataExtractor = (p, history) => {
  return {
    parcel_id: p.id,
    cluster_id: p.cluster_id,
    crop_id: p.current_crop_id,
    current_tenant_cluster: p.current_tenant_cluster?.name,
    error: p.error,
    category: p.category,
    area: p.area,
    status: p.status,
    name: p.name,
    crop_name: p.crop?.name,
    crop_color: p.crop ? `#${p.crop?.color}` : null,
    action: () => history.push(`/parcels/${p.id}`),
    isViti: p.cluster?.activities?.length === 1 && p.cluster.activities[0] === CLUSTER_CONSTANTS.ACTIVITIES.viticulture,
    hasCropPlugin: isPluginActivated(p.cluster, CLUSTER_CONSTANTS.PLUGINS_NAMES.crop_management),
  };
};

export function useDrawParcelsOnMap(
  map,
  parcels,
  events,
  styleBuilder,
  dataExtractor,
  parcelOverTracks,
  parcelsCanvasRenderer,
  parcelsNamesDraggable,
  forceNoMarkerCluster,
) {
  const { units, showParcelNames } = useContext(UIContext);
  const [mapBounds, setMapBounds] = useState(map.getBounds());
  const [mapZoom, setMapZoom] = useState(map.getZoom());

  useEffect(() => {
    const onMapZoomChange = () => setMapZoom(map.getZoom());
    map.on('zoom', onMapZoomChange);
    return () => {
      map.off('zoom', onMapZoomChange);
    };
  }, [map]);

  useEffect(() => {
    const onMapMove = () => setMapBounds(map.getBounds());
    map.on('moveend', onMapMove);
    return () => {
      map.off('moveend', onMapMove);
    };
  }, [map]);

  const zoomIn = useCallback(() => map.zoomIn(), [map]);
  const [parcelNamesPositionMap, setParcelNamesPositionMap] = useState({});
  // sorting of parcels in order to draw parcels with "INACTIVE" status first
  // and client parcels (with cluster_id) last for better selectability
  const sortedParcels = useMemo(() => {
    return parcels?.toSorted((a, b) => {
      if (!a.id && !!b.id) {
        return 1;
      }
      if (!!a.id && !b.id) {
        return -1;
      }

      // Client parcels (with cluster_id) should be drawn last (on top)
      const aIsClientParcel = !!a.cluster_id;
      const bIsClientParcel = !!b.cluster_id;
      if (aIsClientParcel && !bIsClientParcel) {
        return 1;
      }
      if (!aIsClientParcel && bIsClientParcel) {
        return -1;
      }

      if (a.status === PARCELS_CONSTANTS.STATUS.RENT_INACTIVE && b.status !== PARCELS_CONSTANTS.STATUS.RENT_INACTIVE) {
        return -1;
      }
      if (a.status !== PARCELS_CONSTANTS.STATUS.RENT_INACTIVE && b.status === PARCELS_CONSTANTS.STATUS.RENT_INACTIVE) {
        return 1;
      }
      return 0;
    });
  }, [parcels]);

  const patchedParcels = useMemo(() => {
    return (
      sortedParcels &&
      sortedParcels.map((p, index) => {
        const geo = typeof p.geometry_area === 'string' ? JSON.parse(p.geometry_area) : p.geometry_area;
        return {
          ...geo,
          properties: {
            ...geo.properties,
            index,
            ...dataExtractor(p),
          },
        };
      })
    );
  }, [sortedParcels, dataExtractor]);

  const markerModeActivated = useMemo(() => {
    if (forceNoMarkerCluster) {
      return false;
    }
    return patchedParcels && patchedParcels.length > PARCELS_CONSTANTS.PARCEL_MARKER_CLUSTER.THRESHOLD;
  }, [forceNoMarkerCluster, patchedParcels]);

  const showMarkers = useMemo(() => {
    if (forceNoMarkerCluster) {
      return false;
    }
    return Math.round(mapZoom) < PARCELS_CONSTANTS.PARCEL_MARKER_CLUSTER.ZOOM_LIMIT;
  }, [mapZoom, forceNoMarkerCluster]);

  const markers = useMemo(
    () =>
      Leaflet.markerClusterGroup({
        disableClusteringAtZoom: PARCELS_CONSTANTS.PARCEL_MARKER_CLUSTER.ZOOM_LIMIT,
        spiderfyOnMaxZoom: false,
        showCoverageOnHover: true,
        animate: true,
        maxClusterRadius: 100,
        chunkedLoading: true,
        iconCreateFunction: function (cluster) {
          const children = cluster.getAllChildMarkers();
          const area = getTotal(children, 'area');
          return Leaflet.divIcon({
            html: '<div class="marker-cluster"><div>' + formatArea(area, units.area, true) + '</div></div>',
            className: '',
            iconSize: new Leaflet.Point(60, 60),
          });
        },
        polygonOptions: {
          color: color('cyan'),
          weight: 4,
        },
      }),
    [units.area],
  );

  useEffect(() => {
    if (markerModeActivated) {
      const layers = [];
      for (const p of patchedParcels || []) {
        const { bbox } = p;
        if (!bbox) {
          continue;
        }
        const longitude = (bbox[0] + bbox[2]) / 2;
        const latitude = (bbox[1] + bbox[3]) / 2;
        const marker = Leaflet.marker(Leaflet.latLng(latitude, longitude), {
          icon: Leaflet.divIcon({
            html:
              '<div class="marker-cluster"><div>' + formatArea(p.properties.area, units.area, true) + '</div></div>',
            className: '',
            iconSize: new Leaflet.Point(60, 60),
          }),
        });
        marker.on('click', () => zoomIn());
        marker.area = p.properties.area;
        layers.push(marker);
      }
      markers.addLayers(layers);
    }
    return () => {
      markers.clearLayers();
    };
  }, [markers, patchedParcels, zoomIn, units.area, markerModeActivated]);

  const filterParcelCB = useCallback(
    (p) => {
      if (!p.bbox) return true;
      const corner1 = Leaflet.latLng(p.bbox[1], p.bbox[0]),
        corner2 = Leaflet.latLng(p.bbox[3], p.bbox[2]),
        bounds = Leaflet.latLngBounds(corner1, corner2);
      return mapBounds.intersects(bounds);
    },
    [mapBounds],
  );

  const parcelsToDisplay = useMemo(() => {
    if (patchedParcels) {
      if (markerModeActivated && showMarkers) {
        return;
      }
      return patchedParcels.filter(filterParcelCB);
    }
  }, [patchedParcels, filterParcelCB, showMarkers, markerModeActivated]);

  const geoJsonToDisplayLayer = useMemo(() => {
    if (markerModeActivated && showMarkers) return;
    const opts = {
      type: 'parcel',
      pane: 'overlayPane',
      style: styleBuilder,
      onEachFeature: (f, l) => {
        const theParcel = parcels?.find(({ id }) => id === f.properties?.parcel_id);
        Object.keys(events || {}).forEach((event) => {
          l.on(event, (e) => events[event](theParcel, l, e));
        });
      },
    };
    if (parcelsCanvasRenderer) {
      opts.renderer = parcelsCanvasRenderer;
    }
    const parcelLayer = Leaflet.geoJSON(parcelsToDisplay, opts);
    parcelLayer.setStyle(styleBuilder);
    if (parcelOverTracks) {
      parcelLayer.setStyle({
        pane: 'shadowPane',
      });
    } else {
      parcelLayer.setStyle({
        pane: 'overlayPane',
      });
    }
    return parcelLayer;
  }, [
    markerModeActivated,
    showMarkers,
    styleBuilder,
    parcelsCanvasRenderer,
    parcelsToDisplay,
    parcelOverTracks,
    parcels,
    events,
  ]);

  const parcelNames = useMemo(() => {
    if (!geoJsonToDisplayLayer) return;
    const names = [];
    geoJsonToDisplayLayer.eachLayer((l) => {
      const properties = l.feature?.geometry?.properties;
      const name = properties?.name;
      const clusterID = properties?.cluster_id;
      const parcelID = properties?.parcel_id;
      const theHtml = getStaticHTMLFromComponent(<ParcelName name={name} />);
      if (clusterID && l.feature?.geometry?.bbox?.every((el) => el !== 0)) {
        const initialPos = parcelNamesPositionMap[parcelID] || l.getBounds().getCenter();
        const marker = Leaflet.marker(initialPos, {
          draggable: parcelsNamesDraggable,
          icon: Leaflet.divIcon({
            className: 'map-parcel-names',
            html: theHtml,
            bgPos: [0, -5],
          }),
        });
        names.push(marker);
        marker.on('dragend', () => {
          setParcelNamesPositionMap((m) => ({
            ...m,
            [parcelID]: marker.getLatLng(),
          }));
        });
      }
    });
    return Leaflet.layerGroup(names);
  }, [geoJsonToDisplayLayer, parcelsNamesDraggable, parcelNamesPositionMap]);

  const showNames = useMemo(() => {
    return showParcelNames && mapZoom > PARCELS_CONSTANTS.PARCEL_NAMES.ZOOM_LIMIT;
  }, [mapZoom, showParcelNames]);

  useEffect(() => {
    if (!map) return () => {};
    if (markerModeActivated && showMarkers) {
      map.addLayer(markers);
    } else {
      if (geoJsonToDisplayLayer) {
        geoJsonToDisplayLayer.addTo(map);
      }
      if (showNames) {
        map.addLayer(parcelNames);
      }
    }
    return () => {
      markers && markers.removeFrom(map);
      geoJsonToDisplayLayer && geoJsonToDisplayLayer.removeFrom(map);
      parcelNames && parcelNames.removeFrom(map);
    };
  }, [geoJsonToDisplayLayer, map, showMarkers, markers, parcelNames, showNames, markerModeActivated]);

  return geoJsonToDisplayLayer;
}

export function useDrawVectorLayerOnMap(map, datasets, onParcelClick, metadata) {
  const useIGN = useAppSelector(selectUseIgnTiles);

  const [layer, setLayer] = useState(null);

  const minZoom = useMemo(
    () =>
      datasets.length ? Math.min(...datasets.map((d) => d.minZoom ?? PARCELS_CONSTANTS.LAYER_DEFAULT_MIN_ZOOM)) : null,
    [datasets],
  );

  const style = useParcelsDefaultStyle();
  const [metadataString, setMetadataString] = useState(metadata ? JSON.stringify(metadata) : '');
  const setMetadataStringDebounced = useDebounce(setMetadataString, 500);

  useEffect(() => {
    setMetadataStringDebounced(metadata ? JSON.stringify(metadata) : '');
  }, [metadata, setMetadataStringDebounced]);

  useEffect(() => {
    let vectorGrid = null;
    if (datasets.length) {
      const datasetParams = new URLSearchParams(
        metadataString ? { include_metadata: true, metadata: metadataString } : {},
      );
      datasets.forEach((d) => datasetParams.append('dataset_ids', d.id));
      // each dataset comes back as its own tile layer, named after the dataset
      const datasetStyle = { ...style({ geometry: { properties: {} } }), fill: true };
      vectorGrid = Leaflet.vectorGrid
        .protobuf(`${LAYER_API_BASE}/geometries/tiles/{z}/{x}/{y}?${datasetParams.toString()}`, {
          rendererFactory: Leaflet.svg.tile,
          vectorTileLayerStyles: Object.fromEntries(datasets.map((d) => [d.label, datasetStyle])),
          getFeatureId: (f) => f.properties.id,
          interactive: true,
          fetchOptions: {
            headers: {
              Accept: 'application/x-protobuf',
            },
          },
          minZoom,
        })
        .setZIndex(zIndex('ui_map_overlay'))
        .addTo(map);
    }

    setLayer(vectorGrid);

    return () => {
      vectorGrid && vectorGrid.removeFrom(map);
      setLayer(null);
    };
  }, [datasets, map, metadataString, minZoom, style]);

  useEffect(() => {
    const toggleSmoothZoom = () => {
      /* vector tiles don’t work when using smooth zoom */
      if (map.getZoom() > (minZoom ?? PARCELS_CONSTANTS.LAYER_DEFAULT_MIN_ZOOM) - 1 || !useIGN) {
        setSmoothZoom(false, map);
      } else if (useIGN) {
        setSmoothZoom(true, map);
      }
    };

    layer && map.on('zoomend', toggleSmoothZoom);

    return () => {
      map.off('zoomend', toggleSmoothZoom);
    };
  }, [minZoom, layer, map, useIGN]);

  const memoizedGeoJSONs = useRef({});

  const getParcelGeoJSONFromId = useCallback(async (id) => {
    if (id && memoizedGeoJSONs.current[id]) {
      return memoizedGeoJSONs.current[id];
    }

    try {
      const response = await fetch(`${LAYER_API_BASE}/geometries/${id}`, {
        headers: { Accept: 'application/geo+json' },
      });
      const data = await response.json();
      memoizedGeoJSONs.current[id] = { data };
      return { data };
    } catch (e) {
      console.error(e);
      return { error: e };
    }
  }, []);

  useEffect(() => {
    async function onClick(e) {
      const properties = e.propagatedFrom.properties;

      if (properties.id) {
        const response = await getParcelGeoJSONFromId(properties.id);
        if (response.error || response.data.features.length !== 1) {
          return null;
        }
        const parcel = JSON.parse(JSON.stringify(response.data.features[0]));

        // convert MultiPolygon to Polygon
        if (parcel.geometry.type === 'MultiPolygon') {
          parcel.geometry.type = 'Polygon';
          parcel.geometry.coordinates = parcel.geometry.coordinates[0];
        }

        parcel.id = parcel.properties.id;
        parcel.area = parcelArea(parcel.geometry);
        parcel.name = parcel.properties.name;
        parcel.farmhouse = false;

        return onParcelClick(parcel, layer);
      }
    }

    if (layer) {
      layer.on('click', onClick);
    }

    return () => {
      if (layer) {
        layer.off('click', onClick);
      }
    };
  }, [getParcelGeoJSONFromId, layer, onParcelClick]);

  return layer;
}

export function useParcelsCoords(parcels) {
  return useMemo(() => getParcelsCoords(parcels), [parcels]);
}

export function getParcelsCoords(parcels) {
  if (!parcels?.length) return [];

  return parcels
    .map((p) => {
      const geometry = typeof p.geometry_area === 'string' ? JSON.parse(p.geometry_area) : p.geometry_area;
      // Handle FeatureCollection
      if (geometry.type === 'FeatureCollection') {
        if (geometry.features?.length > 0) {
          const feature = geometry.features[0];
          const coordinates = feature.geometry?.coordinates?.[0]?.length
            ? feature.geometry.coordinates
            : feature.coordinates?.[0].length
              ? feature.coordinates
              : [];

          if (coordinates.length) {
            return coordinates[0].map((c) => [c[1], c[0]]);
          }
        }
        return [];
      }

      // Handle Feature
      if (geometry.type === 'Feature' && geometry.geometry?.coordinates?.[0]?.length) {
        return geometry.geometry.coordinates[0].map((c) => [c[1], c[0]]);
      }

      // Handle direct geometry or fallback to bbox
      if (!geometry.bbox) {
        const theCoords = geometry.coordinates?.[0];
        if (theCoords?.length) {
          return theCoords.map((c) => [c[1], c[0]]);
        }
      }

      return [
        [geometry.bbox[1], geometry.bbox[0]],
        [geometry.bbox[3], geometry.bbox[2]],
      ];
    })
    .filter((c) => c.length > 0);
}

/** Default parcel style, without custom colorations */
export function useParcelsDefaultStyle() {
  return useCallback((feature) => buildParcelStyle(feature.geometry.properties), []);
}

/** Parcel colors with custom coloration (by crop, by cluster etc.) */
export function useParcelsOverrideStyle() {
  const getParcelColoration = useParcelColoration();

  const featureStyle = useCallback(
    (feature) => {
      const { parcel_id } = feature.geometry.properties || {};
      const parcelColoration = getParcelColoration(parcel_id);

      if (parcelColoration) {
        return {
          fillColor: parcelColoration,
          fillOpacity: 0.7,
        };
      }

      return {};
    },
    [getParcelColoration],
  );

  return useMemo(() => [featureStyle], [featureStyle]);
}

/** Style to highlight hovered parcels */
export function useHoveredParcelsStyle(hoveredIds) {
  const featureStyle = useCallback(
    (feature) => {
      const { parcel_id } = feature.geometry.properties || {};
      if (parcel_id !== undefined && hoveredIds && hoveredIds.includes(parcel_id.toString())) {
        return {
          fillOpacity: 0.7,
          weight: 6,
          zIndex: 203,
        };
      }

      return {};
    },
    [hoveredIds],
  );

  return useMemo(() => [featureStyle], [featureStyle]);
}

/** Style to highlight selected parcels */
export function useSelectedParcelsFocusedStyle(targetIds = []) {
  const style = useCallback(
    (feature) => {
      const { error, farmhouse, parcel_id } = feature.geometry.properties || {};
      let colorValue = farmhouse ? 'yellow' : 'green';
      if (error) {
        colorValue = 'red';
      }
      return parcel_id !== undefined && targetIds && targetIds.includes(parcel_id.toString())
        ? {
            strokeColor: colors(colorValue, 'light'),
            color: colors(colorValue, 'light'),
            fillColor: colors(colorValue, 'light'),
            fillOpacity: 0.6,
            weight: 2,
            zIndex: 203,
          }
        : {};
    },
    [targetIds],
  );
  return [style];
}

export function useParcelTooltipLabels() {
  const { t } = useContext(I18nContext);
  return useMemo(
    () => ({
      crop_undefined: t('Crop.undefined'),
      being_rent: t('ParcelDetails.being_rent'),
      been_rent: t('ParcelDetails.been_rent'),
      see: t('Commons.see'),
    }),
    [t],
  );
}

export function useWorkedAreaStyle() {
  return useMemo(
    () => [
      (feature) => {
        return !feature.geometry?.properties?.parcel_id
          ? {
              fillColor: colors('white'),
              fillOpacity: 0.5,
              weight: 0,
            }
          : {};
      },
      inertFeatures((feature) => !feature.geometry?.properties?.parcel_id),
    ],
    [],
  );
}
